/** Helpers for link-analysis searchable values and aggregate hubs. */

export interface AggregateItem {
  value: string;
  sources: string[];
  /** Ladder entry: jump target for sidebar row click (R12). */
  sourceNodeId?: string;
  sourceLabel?: string;
}

const ACTIONABLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailHintValue(value: unknown): boolean {
  const text = String(value ?? '').trim();
  if (!text) return true;
  if (/\*{2,}/.test(text)) return true;
  if (/\s\*\*\*/.test(text)) return true;
  // Masked local/domain fragments like "a *** @g *** .com"
  if (/\*{1,}/.test(text) && text.includes('@')) return true;
  if (text.includes('@') && /[Xx]{2,}/.test(text)) return true;
  if (/[Xx]{2,}/.test(text) && /^[Xx\d+\s().-]+$/.test(text)) return true;
  return false;
}

export function isActionableEmailValue(value: unknown): boolean {
  const text = String(value ?? '').trim();
  if (!text || isEmailHintValue(text)) return false;
  return ACTIONABLE_EMAIL_RE.test(text);
}

export function isActionableNameValue(value: unknown): boolean {
  const text = String(value ?? '').trim();
  return text.length >= 2 && !isEmailHintValue(text) &&
    !/\.{2,}|…|\*/u.test(text) && !text.includes('@') && !/^https?:\/\//i.test(text);
}

/**
 * Prefer metadata.searchable, fall back to searchableValues.
 * Email hints are never returned in the email list.
 */
export function getNodeSearchableValues(node: any): {
  phone: string[];
  email: string[];
  username: string[];
  image: string[];
  name: string[];
} {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  const raw = metadata.searchable || metadata.searchableValues || {};
  const asList = (value: unknown): string[] =>
    Array.isArray(value) ? value.map((item) => String(item).trim()).filter(Boolean) : [];

  return {
    phone: asList(raw.phone),
    email: asList(raw.email).filter((email) => isActionableEmailValue(email)),
    username: asList(raw.username),
    image: asList(raw.image),
    name: asList(raw.name || raw.names || metadata.extractedNames).filter(isActionableNameValue),
  };
}

/**
 * Case-insensitive uniqueness key for aggregate names/emails/phones.
 * Names/emails: trim + collapse whitespace + toLowerCase.
 * Phones: digits only.
 */
export function uniqueKeyForAggregateValue(
  kind: string | null | undefined,
  value: unknown,
): string {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const normalizedKind = String(kind || '').toLowerCase();
  if (
    normalizedKind === 'phones' ||
    normalizedKind === 'phone' ||
    normalizedKind === 'aggregate_phones'
  ) {
    return text.replace(/\D/g, '') || text.toLowerCase();
  }
  return text.replace(/\s+/g, ' ').toLowerCase();
}

function resolveAggregateCollapseKind(node: any): string {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  const collectionType = String(metadata.collectionType || '').toLowerCase();
  if (collectionType === 'phone' || collectionType === 'phones') return 'phones';
  if (collectionType === 'email' || collectionType === 'emails') return 'emails';
  if (collectionType === 'name' || collectionType === 'names') return 'names';
  if (collectionType === 'photo' || collectionType === 'image') return 'photos';

  const type = String(node?.type || node?.raw?.type || '').toLowerCase();
  if (type === 'aggregate_phones') return 'phones';
  if (type === 'aggregate_emails') return 'emails';
  if (type === 'aggregate_names') return 'names';
  if (type === 'aggregate_photos') return 'photos';
  return 'names';
}

function pushUniqueSource(bucket: string[], label: string): void {
  const text = String(label || '').trim();
  if (!text) return;
  if (bucket.some((existing) => existing.toLowerCase() === text.toLowerCase())) return;
  bucket.push(text);
}

/**
 * Collapse aggregate entries by case-insensitive key.
 * Prefer first-seen original casing; merge source labels.
 */
export function uniqueAggregateItems(
  items: AggregateItem[],
  kind?: string | null,
): AggregateItem[] {
  const collapseKind = String(kind || 'names').toLowerCase();
  const byKey = new Map<string, AggregateItem>();

  items.forEach((item) => {
    const value = String(item?.value ?? '').trim();
    if (!value) return;
    const key = uniqueKeyForAggregateValue(collapseKind, value);
    if (!key) return;

    const existing = byKey.get(key);
    if (!existing) {
      const sources: string[] = [];
      (item.sources || []).forEach((src) => pushUniqueSource(sources, src));
      if (item.sourceLabel) pushUniqueSource(sources, item.sourceLabel);
      byKey.set(key, {
        value,
        sources,
        ...(item.sourceNodeId ? { sourceNodeId: item.sourceNodeId } : {}),
        ...(item.sourceLabel ? { sourceLabel: item.sourceLabel } : {}),
      });
      return;
    }

    (item.sources || []).forEach((src) => pushUniqueSource(existing.sources, src));
    if (item.sourceLabel) pushUniqueSource(existing.sources, item.sourceLabel);
    if (!existing.sourceNodeId && item.sourceNodeId) {
      existing.sourceNodeId = item.sourceNodeId;
    }
    if (!existing.sourceLabel && item.sourceLabel) {
      existing.sourceLabel = item.sourceLabel;
    }
  });

  return Array.from(byKey.values());
}

/** Normalize one aggregate entry (server entries[] or legacy items[]). */
function mapAggregateEntry(item: unknown): AggregateItem | null {
  const entry = item as Record<string, unknown> | null | undefined;
  const value = String(entry?.['value'] ?? '').trim();
  if (!value) return null;

  const sourceNodeId = String(
    entry?.['sourceNodeId'] ||
      entry?.['entry_source_node_id'] ||
      entry?.['source_node_id'] ||
      '',
  ).trim();
  const sourceLabel = String(
    entry?.['sourceLabel'] || entry?.['source_label'] || '',
  ).trim();
  const rawSources = entry?.['sources'];
  const sources = Array.isArray(rawSources)
    ? rawSources.map((src: unknown) => String(src)).filter(Boolean)
    : sourceLabel
      ? [sourceLabel]
      : [];

  return {
    value,
    sources,
    ...(sourceNodeId ? { sourceNodeId } : {}),
    ...(sourceLabel ? { sourceLabel } : {}),
  };
}

function isAggregateItem(item: AggregateItem | null): item is AggregateItem {
  return Boolean(item);
}

/** Aggregate hub items: prefer entries[], then items[{value,sources}], then collectedValues. */
export function getAggregateItems(node: any): AggregateItem[] {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  const kind = resolveAggregateCollapseKind(node);
  let rawItems: AggregateItem[] = [];

  const fromEntries: unknown[] = Array.isArray(metadata.entries) ? metadata.entries : [];
  if (fromEntries.length) {
    rawItems = fromEntries.map(mapAggregateEntry).filter(isAggregateItem);
  } else {
    const fromItems: unknown[] = Array.isArray(metadata.items) ? metadata.items : [];
    if (fromItems.length) {
      rawItems = fromItems.map(mapAggregateEntry).filter(isAggregateItem);
    } else {
      const collected = Array.isArray(metadata.collectedValues) ? metadata.collectedValues : [];
      rawItems = collected
        .map((value: unknown) => String(value ?? '').trim())
        .filter(Boolean)
        .map((value: string) => ({ value, sources: [] as string[] }));
    }
  }

  return uniqueAggregateItems(rawItems, kind);
}

export function getAggregateCollectedValues(node: any): string[] {
  return getAggregateItems(node).map((item) => item.value);
}

export function isAggregateHubNode(node: any): boolean {
  const type = String(node?.type || '').toLowerCase();
  return Boolean(
    node?.metadata?.isAggregateCollection ||
      type === 'aggregate_phones' ||
      type === 'aggregate_emails' ||
      type === 'aggregate_names' ||
      type === 'aggregate_photos' ||
      String(node?.id || '').startsWith('aggregate:') ||
      String(node?.id || '').startsWith('collection:'),
  );
}

/**
 * Phase 6: server-computed actions (matrix + permissions + service availability).
 * When metadata.availableActions is an array (including empty), the UI must not invent buttons.
 */
export function hasServerAvailableActionsContract(node: any): boolean {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  return Array.isArray(metadata.availableActions);
}

export function getServerAvailableActions(node: any): string[] {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  return Array.isArray(metadata.availableActions)
    ? metadata.availableActions.map((action: unknown) => String(action))
    : [];
}

export function getServerAvailableActionsByValueType(
  node: any,
): Record<string, string[]> {
  const metadata = node?.metadata || node?.raw?.metadata || {};
  const byType = metadata.availableActionsByValueType;
  if (!byType || typeof byType !== 'object') return {};
  const out: Record<string, string[]> = {};
  Object.entries(byType).forEach(([valueType, actions]) => {
    if (!Array.isArray(actions) || !actions.length) return;
    out[String(valueType)] = actions.map((action) => String(action));
  });
  return out;
}

export function getServerActionsForValueType(node: any, valueType: string): string[] | null {
  if (!hasServerAvailableActionsContract(node)) return null;
  const type = String(valueType || '').toLowerCase();
  const byType = getServerAvailableActionsByValueType(node);
  if (Object.prototype.hasOwnProperty.call(byType, type)) {
    return byType[type] || [];
  }
  // Decorated graph with byType present but this value type omitted → no actions.
  if (Object.keys(byType).length > 0) {
    return [];
  }
  // Builders may only set availableActions; filter matrix by the union until decorate.
  const union = new Set(getServerAvailableActions(node));
  if (type === 'image' && String(node?.type || '').toLowerCase() === 'face_image') {
    return [...union].filter((action) => action === 'face_search');
  }
  const matrix: Record<string, string[]> = {
    phone: ['osint_search', 'leak_search'],
    email: ['osint_search', 'leak_search'],
    username: ['osint_search', 'leak_search'],
    name: ['osint_search'],
    image: ['face_search'],
  };
  return (matrix[type] || []).filter((action) => union.has(action));
}
